import React, { useEffect, useRef } from 'react';

import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import { Box } from '@atlaskit/primitives/compiled';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { useAudioPlayerStreamingContext } from '../../../context/AudioPlayerStreamingProvider';
import { getClosedEventAttributes } from '../../../effects/getEventAttributes';
import { useAudioPlayerStore } from '../../../hooks/useAudioPlayerStore';
import { ModalHeader } from '../ModalHeader';

const i18n = defineMessages({
	backButtonLabel: {
		id: 'ai-audio.audio-player.overflow-menu.back-button-label',
		defaultMessage: 'Back',
		description: 'The label for the back button in the overflow menu panel',
	},
	backButtonAriaLabel: {
		id: 'ai-audio.audio-player.overflow-menu.back-button-aria-label',
		defaultMessage: 'Back to audio player',
		description: 'The aria label for the back button in the overflow menu panel',
	},
});

export type PanelWrapperProps = {
	children: React.ReactNode;
	header: MessageDescriptor;
	onBack: () => void;
	closeButtonRef?: React.Ref<HTMLButtonElement>;
};

export const PanelWrapper = ({
	header,
	onBack,
	children,
	closeButtonRef,
}: PanelWrapperProps): React.JSX.Element => {
	const { closeAudioPlayer } = useAudioPlayerStore();
	const { formatMessage } = useIntl();
	const backButtonRef = useRef<HTMLButtonElement>(null);
	const { audioRef } = useAudioPlayerStreamingContext();
	const { trackAIResultAction } = useAIEventsInstrumentation();

	useEffect(() => {
		// Focus the back button when the panel is rendered
		if (backButtonRef.current) {
			backButtonRef.current.focus();
		}
	}, []);

	const handleClose = () => {
		trackAIResultAction('audioClosed', {
			attributes: {
				...getClosedEventAttributes(audioRef, 'closed from overflow menu panel'),
			},
		});
		closeAudioPlayer();
	};

	return (
		<>
			<ModalHeader
				leftElement={
					<IconButton
						ref={backButtonRef}
						icon={ChevronLeftIcon}
						appearance="subtle"
						label={formatMessage(i18n.backButtonAriaLabel)}
						spacing="compact"
						onClick={onBack}
						testId="overflow-menu-panel-back-button"
					/>
				}
				heading={header}
				onClose={handleClose}
				closeButtonRef={closeButtonRef}
			/>
			<Box>{children}</Box>
		</>
	);
};
